/**
 * Утилиты для масок ввода.
 *
 * options.mask — регулярка для сохраняемого (сырого) значения, напр. \\d{11}, \\d{10}, [A-Z]{1}\\d{3}[A-Z]{2}\\d{2,3}.
 * options.displayFormat — шаблон отображения: 0 = цифра, X = буква, (0)/(X) = опционально, остальное = литералы.
 *
 * Примеры:
 * - СНИЛС: displayFormat "000-000-000 00", mask "\\d{11}"
 * - Телефон: displayFormat "+7 (000) 000-00-00", mask "\\d{10}"
 * - Номер авто: displayFormat "X000XX00(0)", mask "[A-Z]{1}\\d{3}[A-Z]{2}\\d{2,3}"
 */

const DIGIT = '0'
const LETTER = 'X'
const OPTIONAL_DIGIT = '(0)'
const OPTIONAL_LETTER = '(X)'

function isDigit(c: string): boolean {
  return /\d/.test(c)
}

function isLetter(c: string): boolean {
  return /[a-zA-Zа-яА-Я]/.test(c)
}

/** Тип плейсхолдера в шаблоне */
type PlaceholderType = 'digit' | 'letter'

/**
 * Разбирает шаблон в список плейсхолдеров по порядку (для извлечения значения из ввода).
 */
function getTemplatePlaceholders(
  pattern: string,
): Array<{ type: PlaceholderType; optional?: boolean }> {
  const result: Array<{ type: PlaceholderType; optional?: boolean }> = []
  let i = 0
  while (i < pattern.length) {
    if (pattern.substring(i, i + 3) === OPTIONAL_DIGIT) {
      result.push({ type: 'digit', optional: true })
      i += 3
    } else if (pattern.substring(i, i + 3) === OPTIONAL_LETTER) {
      result.push({ type: 'letter', optional: true })
      i += 3
    } else if (pattern[i] === DIGIT) {
      result.push({ type: 'digit' })
      i += 1
    } else if (pattern[i] === LETTER) {
      result.push({ type: 'letter' })
      i += 1
    } else {
      i += 1
    }
  }
  return result
}

/**
 * Извлечь из ввода символы по шаблону (0 = цифра, X = буква) в порядке шаблона, ограничить длиной.
 */
export function parseDisplayFormatValue(input: string, pattern: string): string {
  const placeholders = getTemplatePlaceholders(pattern)
  const inputChars = (input || '').split('').filter(c => isDigit(c) || isLetter(c))
  const result: string[] = []
  let inputPos = 0
  for (const ph of placeholders) {
    while (inputPos < inputChars.length) {
      const c = inputChars[inputPos]
      if ((ph.type === 'digit' && isDigit(c)) || (ph.type === 'letter' && isLetter(c))) {
        result.push(c)
        inputPos++
        break
      }
      inputPos++
    }
    if (ph.optional && inputPos >= inputChars.length) break
  }
  return result.join('')
}

/**
 * Отформатировать сырое значение по шаблону.
 * Литералы добавляются только когда после них уже есть следующий символ (0123 → 012-3).
 */
export function formatWithDisplayPattern(value: string, pattern: string): string {
  const valueChars = (value || '').split('').filter(c => isDigit(c) || isLetter(c))
  let valuePos = 0
  let result = ''
  let i = 0
  while (i < pattern.length) {
    if (pattern.substring(i, i + 3) === OPTIONAL_DIGIT) {
      if (valueChars[valuePos] && isDigit(valueChars[valuePos])) {
        result += valueChars[valuePos]
        valuePos++
      }
      i += 3
    } else if (pattern.substring(i, i + 3) === OPTIONAL_LETTER) {
      if (valueChars[valuePos] && isLetter(valueChars[valuePos])) {
        result += valueChars[valuePos]
        valuePos++
      }
      i += 3
    } else if (pattern[i] === DIGIT) {
      result += valueChars[valuePos] ?? ''
      valuePos++
      i += 1
    } else if (pattern[i] === LETTER) {
      result += valueChars[valuePos] ?? ''
      valuePos++
      i += 1
    } else {
      if (valuePos < valueChars.length) {
        result += pattern[i]
      }
      i += 1
    }
  }
  return result
}

/**
 * Проверяет, является ли строка валидной регуляркой (для маски с бэка).
 */
export function isMaskRegExp(mask: string): boolean {
  if (typeof mask !== 'string' || !mask.length) return false
  try {
    new RegExp(mask)
    return true
  } catch {
    return false
  }
}

/**
 * Валидация сохраняемого значения по регулярке options.mask.
 */
export function validateWithMaskRegex(value: string, maskRegExp: RegExp): boolean {
  if (value == null || value === '') return true
  return maskRegExp.test(String(value))
}

/**
 * Сформировать плейсхолдер для поля по шаблону displayFormat: 0/X/(0)/(X) заменяются на _.
 */
export function getPlaceholderFromFormat(pattern: string): string {
  let result = ''
  let i = 0
  while (i < pattern.length) {
    if (
      pattern.substring(i, i + 3) === OPTIONAL_DIGIT ||
      pattern.substring(i, i + 3) === OPTIONAL_LETTER
    ) {
      result += '(_)'
      i += 3
    } else if (pattern[i] === DIGIT || pattern[i] === LETTER) {
      result += '_'
      i += 1
    } else {
      result += pattern[i]
      i += 1
    }
  }
  return result
}
